/**
 * First-party usage telemetry + rrweb session replays.
 *
 * Events are batched in memory and flushed every FLUSH_INTERVAL_MS and on
 * `pagehide`. The backend attaches the player's identity from the auth token.
 * `navigator.sendBeacon` can't carry an Authorization header, so the pagehide
 * flush uses `fetch(..., { keepalive: true })`, which survives unload the same way.
 *
 * Everything here is best effort: failures are swallowed, nothing blocks the UI,
 * and once the backend answers 404/403 (endpoints not deployed yet) or the user
 * opted out, telemetry switches itself off for the rest of the page life.
 */
import { OpenAPI } from '../generated-api';

export type TelemetryEventType = 'page_view' | 'click' | 'submit' | 'action' | 'error' | 'api' | 'vital';

export interface TelemetryEvent {
  type: TelemetryEventType;
  name: string;
  ts: string;
  route?: string;
  value?: number | null;
  props?: Record<string, unknown> | null;
}

export const FLUSH_INTERVAL_MS = 10_000;
export const MAX_BATCH = 100;
export const MAX_QUEUE = 1000;
export const MAX_CLICK_LABEL = 32;
const MAX_TRACK_NAME = 80;
const REPLAY_FLUSH_EVENTS = 300;
const MAX_SERVER_ERRORS = 3;
const SESSION_KEY = 'bgs.telemetry.session';
const OPT_OUT_KEY = 'bgs.telemetry.optOut';

interface State {
  enabled: boolean;
  started: boolean;
  queue: TelemetryEvent[];
  replayQueue: unknown[];
  replaySeq: number;
  sessionId: string;
  playerId: string | null;
  timer: ReturnType<typeof setInterval> | null;
  stopReplay: (() => void) | null;
  serverErrors: number;
}

const state: State = {
  enabled: false,
  started: false,
  queue: [],
  replayQueue: [],
  replaySeq: 0,
  sessionId: '',
  playerId: null,
  timer: null,
  stopReplay: null,
  serverErrors: 0,
};

const randomId = (): string => {
  const bytes = new Uint8Array(12);
  const cryptoApi = globalThis.crypto || (window as any).msCrypto;
  if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return 'sess_' + Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

export const getSessionId = (): string => {
  if (state.sessionId) return state.sessionId;
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    state.sessionId = existing || randomId();
    if (!existing) sessionStorage.setItem(SESSION_KEY, state.sessionId);
  } catch {
    state.sessionId = randomId();
  }
  return state.sessionId;
};

export const deviceType = (width: number = window.innerWidth): 'mobile' | 'tablet' | 'desktop' =>
  width < 640 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop';

export const isOptedOut = (): boolean => {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === 'true';
  } catch {
    return false;
  }
};

const resolveToken = async (): Promise<string | undefined> => {
  const token = OpenAPI.TOKEN;
  if (!token) return undefined;
  return typeof token === 'function' ? token({} as any) : token;
};

const post = async (path: string, body: unknown, keepalive = false): Promise<void> => {
  const token = await resolveToken();
  if (!token) return;
  const res = await fetch(`${OpenAPI.BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
    keepalive,
  });
  if (res.status === 404 || res.status === 403) {
    // Endpoints not deployed yet (or not allowed): stop trying for this page life.
    disable();
  } else if (res.status >= 500) {
    state.serverErrors += 1;
    if (state.serverErrors >= MAX_SERVER_ERRORS) disable();
  } else {
    state.serverErrors = 0;
  }
};

export const track = (
  type: TelemetryEventType,
  name: string,
  extra: { value?: number | null; props?: Record<string, unknown> } = {},
): void => {
  if (!state.enabled) return;
  if (state.queue.length >= MAX_QUEUE) state.queue.shift();
  state.queue.push({
    type,
    name: String(name).slice(0, 200),
    ts: new Date().toISOString(),
    route: window.location.pathname,
    value: extra.value ?? null,
    props: extra.props ?? null,
  });
};

/** Collapse ids in a path so pages group together: /plays/abc123 -> /plays/:id. */
export const normalizePath = (path: string): string =>
  path
    .split('?')[0]
    .split('/')
    .map(seg => (/^(new|edit|me)$/.test(seg) || !/\d/.test(seg) || seg.length < 6 ? seg : ':id'))
    .join('/') || '/';

export const trackPageView = (path: string = window.location.pathname): void =>
  track('page_view', normalizePath(path));

/**
 * Times every generated-API call (axios) and records it as an `api` event named
 * "METHOD /path/:id". Telemetry's own posts use fetch, so they aren't counted.
 */
export const installApiTiming = (client: {
  interceptors: {
    request: { use: (f: (c: any) => any) => unknown };
    response: { use: (ok: (r: any) => any, err: (e: any) => any) => unknown };
  };
}): void => {
  const record = (config: any, status: number | undefined) => {
    if (!config || !config.__telemetryStart) return;
    let path = String(config.url || '');
    try {
      path = new URL(path, window.location.origin).pathname;
    } catch {
      // keep raw
    }
    const ms = Math.round(performance.now() - config.__telemetryStart);
    const ok = status !== undefined && status < 400;
    track('api', `${String(config.method || 'get').toUpperCase()} ${normalizePath(path)}`, {
      value: ms,
      props: { ok, status: status ?? 0 },
    });
  };
  client.interceptors.request.use(config => {
    config.__telemetryStart = performance.now();
    return config;
  });
  client.interceptors.response.use(
    response => {
      record(response.config, response.status);
      return response;
    },
    error => {
      record(error?.config, error?.response?.status);
      return Promise.reject(error);
    },
  );
};

/** Key product actions, e.g. trackAction('play_recorded', { game }). */
export const trackAction = (name: string, props?: Record<string, unknown>): void => track('action', name, { props });

/** Set at build time by the deploy pipeline; local and test builds report "dev". */
export const appVersion = (): string => process.env.REACT_APP_VERSION || 'dev';

export const flush = async (keepalive = false): Promise<void> => {
  if (!state.enabled) return;
  const jobs: Promise<void>[] = [];
  while (state.queue.length) {
    const events = state.queue.splice(0, MAX_BATCH);
    jobs.push(
      post(
        '/telemetry/events',
        {
          session_id: getSessionId(),
          app_version: appVersion(),
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          device_type: deviceType(),
          player_id: state.playerId,
          events,
        },
        keepalive,
      ).catch(() => undefined),
    );
  }
  if (state.replayQueue.length) jobs.push(flushReplay(keepalive));
  await Promise.all(jobs);
};

export const gzipBase64 = async (text: string): Promise<string | null> => {
  const CS = (globalThis as any).CompressionStream;
  if (!CS) return null;
  const stream = new Blob([text]).stream().pipeThrough(new CS('gzip'));
  const buf = new Uint8Array(await new Response(stream).arrayBuffer());
  let binary = '';
  for (let i = 0; i < buf.length; i += 0x8000) binary += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(binary);
};

const flushReplay = async (keepalive = false): Promise<void> => {
  const events = state.replayQueue.splice(0);
  if (!events.length) return;
  const json = JSON.stringify(events);
  const gz = await gzipBase64(json).catch(() => null);
  await post(
    '/telemetry/replay',
    { session_id: getSessionId(), seq: state.replaySeq++, encoding: gz ? 'gzip-base64' : 'json', data: gz ?? json },
    keepalive,
  ).catch(() => undefined);
};

/** Route of an in-app link, grouped by id: "link:/plays/:id". External links never expose their URL. */
const describeLink = (el: Element): string | null => {
  const href = el.tagName === 'A' ? el.getAttribute('href') : null;
  if (!href || href.startsWith('#')) return null;
  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin ? `link:${normalizePath(url.pathname)}` : 'link:external';
  } catch {
    return null;
  }
};

/**
 * Short, stable click name. The nearest data-track wins; untagged elements fall back
 * to their link route, then to a short label. Text longer than MAX_CLICK_LABEL is
 * content (a card, a sentence), not a label, so it is dropped rather than recorded:
 * it wouldn't aggregate and it can hold player-entered text.
 */
const describeTarget = (el: Element): string | null => {
  const target = el.closest('[data-track], button, a, [role="button"]');
  const tracked = target?.getAttribute('data-track');
  if (tracked) return tracked.slice(0, MAX_CLICK_LABEL);
  if (el.tagName === 'CANVAS') return 'chart';
  if (!target) return null;
  const link = describeLink(target);
  if (link) return link.slice(0, MAX_TRACK_NAME);
  const label = (target.getAttribute('aria-label') || target.getAttribute('title') || target.textContent || '')
    .trim()
    .replace(/\s+/g, ' ');
  return label && label.length <= MAX_CLICK_LABEL ? label : target.tagName.toLowerCase();
};

const onClick = (e: MouseEvent) => {
  const name = e.target instanceof Element ? describeTarget(e.target) : null;
  if (name) track('click', name);
};
/** Filters and toggles: selects, checkboxes and radios. Typed text is never read. */
const onChange = (e: Event) => {
  const el = e.target as HTMLInputElement | HTMLSelectElement;
  if (!(el instanceof Element)) return;
  const isSelect = el.tagName === 'SELECT';
  const isToggle = el.tagName === 'INPUT' && ['checkbox', 'radio'].includes((el as HTMLInputElement).type);
  if (!isSelect && !isToggle) return;
  const name =
    el.closest('[data-track]')?.getAttribute('data-track') ||
    el.getAttribute('aria-label') ||
    el.getAttribute('name') ||
    el.id ||
    el.tagName.toLowerCase();
  track('action', 'filter_changed', { props: { filter: name.slice(0, 60), value: isSelect ? String(el.value).slice(0, 60) : (el as HTMLInputElement).checked } });
};
const onSubmit = (e: Event) => {
  const form = e.target as HTMLFormElement;
  track('submit', form.getAttribute('data-track') || form.getAttribute('name') || form.id || 'form');
};
const onError = (e: ErrorEvent) => track('error', e.message || 'error', { props: { source: e.filename, line: e.lineno } });
const onRejection = (e: PromiseRejectionEvent) =>
  track('error', `unhandled: ${String((e.reason && e.reason.message) || e.reason).slice(0, 150)}`);
const onPageHide = () => {
  void flush(true);
};
const onVisibility = () => {
  if (document.visibilityState === 'hidden') void flush(true);
};

const startReplay = async () => {
  try {
    const { record } = await import('rrweb');
    const stop = record({
      emit: (event: unknown) => {
        state.replayQueue.push(event);
        if (state.replayQueue.length >= REPLAY_FLUSH_EVENTS) void flushReplay();
      },
      maskAllInputs: true,
      maskInputOptions: { password: true },
      blockClass: 'telemetry-block',
      sampling: { mousemove: 50, scroll: 150, input: 'last' },
    });
    state.stopReplay = stop || null;
  } catch {
    // Replay is optional; events still flow.
  }
};

const startVitals = async () => {
  try {
    const { getCLS, getLCP, getFID, getTTFB } = await import('web-vitals');
    const report = ({ name, value }: { name: string; value: number }) => track('vital', name, { value });
    getCLS(report);
    getLCP(report);
    getFID(report);
    getTTFB(report);
  } catch {
    // ignore
  }
};

/** Start telemetry for a logged-in player. Safe to call more than once. */
export const startTelemetry = (opts: { playerId?: string | null; replay?: boolean } = {}): void => {
  state.playerId = opts.playerId ?? state.playerId;
  if (state.started || isOptedOut() || typeof window === 'undefined') return;
  state.started = true;
  state.enabled = true;
  getSessionId();
  document.addEventListener('click', onClick, { capture: true, passive: true });
  document.addEventListener('submit', onSubmit, { capture: true });
  document.addEventListener('change', onChange, { capture: true, passive: true });
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibility);
  state.timer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  void startVitals();
  if (opts.replay !== false) void startReplay();
};

export const disable = (): void => {
  state.enabled = false;
  state.queue = [];
  state.replayQueue = [];
  if (state.timer) clearInterval(state.timer);
  state.timer = null;
  state.stopReplay?.();
  state.stopReplay = null;
  document.removeEventListener('click', onClick, { capture: true } as EventListenerOptions);
  document.removeEventListener('submit', onSubmit, { capture: true } as EventListenerOptions);
  document.removeEventListener('change', onChange, { capture: true } as EventListenerOptions);
  window.removeEventListener('error', onError);
  window.removeEventListener('unhandledrejection', onRejection);
  window.removeEventListener('pagehide', onPageHide);
  document.removeEventListener('visibilitychange', onVisibility);
};

/** Per-user opt-out: stored locally (stops recording immediately) and on the server. */
export const setOptOut = (optedOut: boolean): void => {
  try {
    localStorage.setItem(OPT_OUT_KEY, String(optedOut));
  } catch {
    // ignore
  }
  if (optedOut) disable();
  else state.started = false; // lets the next startTelemetry() resume
};

/** Test helper. */
export const __resetForTests = (): void => {
  disable();
  state.started = false;
  state.sessionId = '';
  state.replaySeq = 0;
  state.playerId = null;
  state.serverErrors = 0;
};
export const __queueForTests = (): TelemetryEvent[] => state.queue;
